import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ClinicalDocumentsPdfService,
  DocumentPatientInfo,
  NormalizedRecommendations,
} from '../../services/clinical-documents-pdf.service';
import {
  getMedicalNecessityIndicator,
  getMedicalNecessityIndicatorLabel,
  MedicalNecessityIndicator,
  buildCoverageDetails,
  CoverageDetailRow,
} from '../../utils/medical-necessity.util';

export type DocumentViewerType = 'doctor-notes' | 'transcription' | 'medical-necessity' | 'billing-summary';

interface BillingSummaryCodeRow {
  code: string;
  description: string;
  icd10: string;
  reason: string;
  isAddOn: boolean;
}

interface OrderCptCodeRow {
  code: string;
  reason: string;
}

@Component({
  selector: 'app-document-viewer-modal',
  templateUrl: './document-viewer-modal.component.html',
  styleUrls: ['./document-viewer-modal.component.scss'],
})
export class DocumentViewerModalComponent implements OnInit {
  @Input() docType: DocumentViewerType = 'doctor-notes';
  @Input() timeLabel = '';
  @Input() patientName = '';
  @Input() patientMrn = '';
  @Input() providerName = '';

  // doctor-notes
  @Input() rawDoctorNotes: any = null;

  // transcription
  @Input() transcriptionText = '';

  // medical-necessity — download is delegated to the parent since it needs an
  // async ORT-masters fetch (see MedicalNecessityPdfService.generate()).
  @Input() actionItem: any = null;
  @Output() download = new EventEmitter<void>();

  // billing-summary
  @Input() billingSummary: any = null;

  doctorNotes: any = {};
  actionItems: any[] = [];
  recommendations: NormalizedRecommendations | null = null;
  assessmentPoints: string[] = [];
  speakerTurns: { label: string; idx: number; text: string }[] = [];
  coverageDetails: CoverageDetailRow[] = [];
  billingCodeRows: BillingSummaryCodeRow[] = [];
  billingReport = '';
  orderCptCodes: OrderCptCodeRow[] = [];
  mnIndicator: MedicalNecessityIndicator = null;
  mnIndicatorLabel = '';

  constructor(
    public activeModal: NgbActiveModal,
    private clinicalDocumentsPdfService: ClinicalDocumentsPdfService
  ) {}

  ngOnInit(): void {
    if (this.docType === 'doctor-notes' && this.rawDoctorNotes) {
      const parsedData =
        typeof this.rawDoctorNotes === 'string' ? this.safeParse(this.rawDoctorNotes) : this.rawDoctorNotes;
      this.doctorNotes = parsedData?.doctor_notes ?? {};
      this.actionItems = parsedData?.action_items ?? [];
      this.recommendations = this.clinicalDocumentsPdfService.normalizeRecommendations(parsedData);
      this.assessmentPoints = this.clinicalDocumentsPdfService.normalizeAssessmentPoints(parsedData);
    }

    if (this.docType === 'transcription' && this.transcriptionText) {
      this.speakerTurns = this.clinicalDocumentsPdfService.parseSpeakerTurns(this.transcriptionText);
    }

    if (this.docType === 'medical-necessity' && this.actionItem) {
      this.coverageDetails = buildCoverageDetails(this.actionItem.order?.lcd_coverage_details);
      this.orderCptCodes = (this.actionItem.order?.billing_codes || [])
        .filter((c: any) => c.code_type === 'cpt')
        .map((c: any) => ({ code: c.code, reason: c.reason || '' }));
      this.mnIndicator = getMedicalNecessityIndicator(this.actionItem.order);
      this.mnIndicatorLabel = getMedicalNecessityIndicatorLabel(this.mnIndicator);
    }

    if (this.docType === 'billing-summary' && this.billingSummary) {
      const toRow = (c: any, isAddOn: boolean): BillingSummaryCodeRow => ({
        code: c.cpt_code || '',
        description: c.description || '',
        icd10: Array.isArray(c.icd10_codes) ? c.icd10_codes.join(', ') : c.icd10_codes || '',
        reason: c.reason || '',
        isAddOn,
      });
      this.billingCodeRows = [
        ...(this.billingSummary.cpt_codes || []).map((c: any) => toRow(c, false)),
        ...(this.billingSummary.add_on_codes || []).map((c: any) => toRow(c, true)),
      ];
      this.billingReport = this.billingSummary.billing_report || '';
    }
  }

  private safeParse(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  get hasRecommendations(): boolean {
    const r = this.recommendations;
    return !!(
      r &&
      (r.tests.length || r.medications.length || r.treatments.length || r.risk_factors.length || r.considerations)
    );
  }

  get hasDoctorNotesContent(): boolean {
    return !!(
      this.doctorNotes['History Of Present Illness'] ||
      this.doctorNotes['Physical Examination'] ||
      this.doctorNotes['Diagnostics'] ||
      this.assessmentPoints.length ||
      this.hasRecommendations ||
      this.actionItems.length
    );
  }

  get hasMedicalNecessityContent(): boolean {
    return !!(
      this.actionItem?.action_item ||
      this.actionItem?.order?.lcd_coverage ||
      this.coverageDetails.length ||
      this.orderCptCodes.length
    );
  }

  get hasBillingSummaryContent(): boolean {
    return !!(this.billingCodeRows.length || this.billingReport);
  }

  get modalTitle(): string {
    switch (this.docType) {
      case 'doctor-notes':
        return 'Doctor Notes';
      case 'transcription':
        return 'Visit Transcription';
      case 'medical-necessity':
        return 'Medical Necessity';
      case 'billing-summary':
        return 'Billing Summary';
      default:
        return 'Document';
    }
  }

  get modalIcon(): string {
    switch (this.docType) {
      case 'doctor-notes':
        return 'fa-notes-medical';
      case 'transcription':
        return 'fa-microphone-alt';
      case 'medical-necessity':
        return 'fa-shield-alt';
      case 'billing-summary':
        return 'fa-file-invoice-dollar';
      default:
        return 'fa-file-alt';
    }
  }

  speakerClass(idx: number): string {
    return idx >= 0 ? `speaker-${idx % 5}` : 'speaker-na';
  }

  onDownload(): void {
    const info: DocumentPatientInfo = {
      patientName: this.patientName,
      patientMrn: this.patientMrn,
      providerName: this.providerName,
    };

    if (this.docType === 'doctor-notes') {
      this.clinicalDocumentsPdfService.generateDoctorNotesPdf(this.rawDoctorNotes, info);
    } else if (this.docType === 'transcription') {
      this.clinicalDocumentsPdfService.generateTranscriptionPdf(this.transcriptionText, info);
    } else if (this.docType === 'medical-necessity') {
      this.download.emit();
    } else if (this.docType === 'billing-summary') {
      this.clinicalDocumentsPdfService.generateBillingSummaryPdf(this.billingSummary, info);
    }
  }
}
